// src\components\layout\TraceabilityMain.js

/**
 * Layout principal para la sección de trazabilidad.
 *
 * Responsabilidades:
 * - Renderiza el Sidebar lateral y controla si está expandido o colapsado.
 * - Ajusta el margen izquierdo del contenido según el estado del Sidebar.
 * - Muestra el Header con botón de actualizar y acceso rápido a Dashboard.
 * - Recibe `children` y los pinta dentro del área principal de contenido.
 *
 * Props:
 * - children: contenido específico de cada pantalla de trazabilidad
 *   (por ejemplo, TraceabilityPage).
 */

import React, { useState, useCallback } from 'react';
import Sidebar from '../Sidebar';
import '../../../App.css';
import { Home, Menu } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import SidebarPopupContainer from '../../popups/SidebarPopupContainer';
import { globalVehiculosState } from '../../../services/vehiculosGlobalState';

const TraceabilityMain = ({ children }) => {
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(() => new Date());

  const navigate = useNavigate();
  const [activePopup, setActivePopup] = useState(null);
  const [facturadasInitialMarca, setFacturadasInitialMarca] = useState(null);

  const handleSidebarExpandChange = (expanded) => {
    setSidebarExpanded(expanded);
  };

  const handleRefresh = useCallback(async () => {
    setIsUpdating(true);
    try {
      // Refresh global vehicle state data
      await globalVehiculosState.reload();
      // Dispatch custom event to notify child components (like TraceabilityPage) to refresh their specific data
      window.dispatchEvent(new CustomEvent('refreshTraceabilityData'));
      setLastUpdateTime(new Date());
    } catch (error) {
      console.error('Error al refrescar trazabilidad:', error);
    } finally {
      setIsUpdating(false);
    }
  }, []);

  // Popup handlers
  const handleUserManagementClick = useCallback(() => {
    setActivePopup('users');
  }, []);

  const handlePatiosManagementClick = useCallback(() => {
    setActivePopup('patios');
  }, []);

  const handleRoleManagementClick = useCallback(() => {
    setActivePopup('roles');
  }, []);

  const handleUnidadesNoMayoreadasClick = useCallback(() => {
    setActivePopup('unidadesNoMayoreadas');
  }, []);

  const handleFacturadasNoEntregadasClick = useCallback(() => {
    setFacturadasInitialMarca(null);
    setActivePopup('facturadas');
  }, []);

  const handleVendorsManagementClick = useCallback(() => {
    setActivePopup('vendors');
  }, []);

  const handleHistoryClick = useCallback(() => {
    setActivePopup('history');
  }, []);

  const handleSimulateLectureClick = useCallback(() => {
    setActivePopup('lecture');
  }, []);

  // NEW: Navigation handlers for Sidebar items
  const handleMarcaClick = useCallback((marca) => {
    navigate('/dashboard', { state: { selectedMarca: marca } });
  }, [navigate]);

  const handleStatisticClick = useCallback((marca, statisticType) => {
    // Si se cliquea "Facturados no entregados", abrir popup directamente en la página actual
    if (statisticType === 'facturados') {
      setFacturadasInitialMarca(marca);
      setActivePopup('facturadas');
      return;
    }

    // Para inventario y traspasos, ir al dashboard con filtros
    navigate('/dashboard', {
      state: {
        selectedMarca: marca,
        statisticType: statisticType
      }
    });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar
        isExpandedExternal={sidebarExpanded}
        onExpandChange={handleSidebarExpandChange}
        initialView="logistica"
        onUserManagementClick={handleUserManagementClick}
        onPatiosManagementClick={handlePatiosManagementClick}
        onRoleManagementClick={handleRoleManagementClick}
        onUnidadesNoMayoreadasClick={handleUnidadesNoMayoreadasClick}
        onFacturadasNoEntregadasClick={handleFacturadasNoEntregadasClick}
        onVendorsManagementClick={handleVendorsManagementClick}
        onHistoryClick={handleHistoryClick}
        onSimulateLectureClick={handleSimulateLectureClick}
        onMarcaClick={handleMarcaClick}
        onStatisticClick={handleStatisticClick}
      />

      {/* Mobile menu button — top-right, visible only on small screens */}
      {!sidebarExpanded && (
        <button
          onClick={() => setSidebarExpanded(true)}
          className="fixed top-3 right-3 z-40 p-2 bg-white rounded-lg shadow-md sm:hidden"
          aria-label="Abrir menú"
        >
          <Menu className="w-5 h-5 text-gray-700" />
        </button>
      )}

      <div
        className={`transition-all duration-300 ease-in-out p-3 sm:p-4 lg:p-6 ${sidebarExpanded ? 'sm:ml-80' : 'sm:ml-16'
          }`}
      >
        <div className="max-w-none px-1 sm:px-2 lg:px-4" style={{ textAlign: 'left' }}>
          <Header
            onLogout={null}
            onRefresh={handleRefresh}
            isUpdating={isUpdating}
            lastUpdateTime={lastUpdateTime}
            secondaryActionLabel="Ver Dashboard"
            onSecondaryAction={() => navigate('/dashboard')}
            secondaryIcon={Home}
          />

          <main className="page-transition-enter">
            {children}
          </main>
        </div>
      </div>

      <SidebarPopupContainer
        activePopup={activePopup}
        onClose={() => setActivePopup(null)}
        facturadasInitialMarca={facturadasInitialMarca}
      />
    </div>
  );
};

export default TraceabilityMain;